import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { SystemUserRole, SYSTEM_ROLES } from "@/schemas/systemUserRole";
import {
  useUsers,
  useUpdateUserStatus,
  useUpdateUserRole,
} from "@/hooks/useUsers";
import { useTeamInvite } from "@/hooks/useTeamInvite";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { UserPlus, Trash2, X, Plus, MoreHorizontal, UserCheck, UserX } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const UserManagement = () => {
  const { isAdmin } = useAuth();

  // React Query hooks
  const { data: users = [], isLoading: loading } = useUsers();
  const updateUserStatusMutation = useUpdateUserStatus();
  const updateUserRoleMutation = useUpdateUserRole();
  const teamInviteMutation = useTeamInvite();

  // Local state for invite form
  const [inviteEmails, setInviteEmails] = useState<string[]>([""]);
  const [emailErrors, setEmailErrors] = useState<(string | null)[]>([null]);
  const [selectedRole, setSelectedRole] = useState<SystemUserRole>(
    SYSTEM_ROLES.USER
  );
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...inviteEmails];
    newEmails[index] = value;
    setInviteEmails(newEmails);

    // Clear error when email changes
    const newErrors = [...emailErrors];
    newErrors[index] = null;
    setEmailErrors(newErrors);
  };

  const handleAddEmail = () => {
    setInviteEmails([...inviteEmails, ""]);
    setEmailErrors([...emailErrors, null]);
  };

  const handleRemoveEmail = (index: number) => {
    if (inviteEmails.length <= 1) return;
    setInviteEmails(inviteEmails.filter((_, i) => i !== index));
    setEmailErrors(emailErrors.filter((_, i) => i !== index));
  };

  const resetInviteForm = () => {
    setInviteEmails([""]);
    setEmailErrors([null]);
    setSelectedRole(SYSTEM_ROLES.USER);
  };

  // 切換用戶狀態
  const toggleUserStatus = async (userId: string, currentStatus: boolean) => {
    updateUserStatusMutation.mutateAsync(
      { userId, isActive: !currentStatus },
      {
        onSuccess: () => {
          toast({
            title: "成功",
            description: `用戶已${!currentStatus ? "啟用" : "停用"}`,
          });
        },
        onError: () => {
          toast({
            title: "錯誤",
            description: "無法更新用戶狀態",
            variant: "destructive",
          });
        },
      }
    );
  };

  // 更新用戶角色
  const updateUserRole = async (userId: string, newRole: SystemUserRole) => {
    // Check if trying to demote the last admin
    if (newRole === SYSTEM_ROLES.USER) {
      const currentUser = users.find(u => u.id === userId);
      if (currentUser?.role === SYSTEM_ROLES.ADMIN) {
        const adminCount = users.filter(u => u.role === SYSTEM_ROLES.ADMIN).length;
        if (adminCount <= 1) {
          toast({
            title: "操作失敗",
            description: "系統至少需要保留一位管理員",
            variant: "destructive",
          });
          return;
        }
      }
    }

    updateUserRoleMutation.mutateAsync(
      { userId, role: newRole },
      {
        onSuccess: () => {
          toast({
            title: "成功",
            description: `用戶角色已更新為${getRoleDisplayName(newRole)}`,
          });
        },
        onError: () => {
          toast({
            title: "錯誤",
            description: "無法更新用戶角色",
            variant: "destructive",
          });
        },
      }
    );
  };

  // 邀請用戶
  const inviteUsers = async () => {
    // Validate emails
    const validEmails = inviteEmails.filter((email) => email.trim());
    const newErrors = inviteEmails.map((email) => {
      if (!email.trim()) return null;
      return validateEmail(email) ? null : "請輸入有效的email地址";
    });

    setEmailErrors(newErrors);

    if (validEmails.length === 0) {
      toast({
        title: "錯誤",
        description: "請輸入至少一個email地址",
        variant: "destructive",
      });
      return;
    }

    if (newErrors.some((error) => error !== null)) {
      toast({
        title: "錯誤",
        description: "請修正email格式錯誤",
        variant: "destructive",
      });
      return;
    }

    teamInviteMutation.mutateAsync(
      {
        emails: validEmails,
        role: selectedRole,
      },
      {
        onSuccess: ({ results, successfulCount, failedCount }) => {
          // 處理個別錯誤
          const fieldErrors = [...emailErrors];
          results.forEach((result) => {
            const emailIndex = inviteEmails.findIndex(
              (email) =>
                email.trim().toLowerCase() === result.email.toLowerCase()
            );
            if (!result.success && emailIndex !== -1) {
              fieldErrors[emailIndex] = result.error;
            }
          });
          setEmailErrors(fieldErrors);

          if (successfulCount > 0) {
            toast({
              title: "成功",
              description: `已成功邀請 ${successfulCount} 位用戶`,
            });
          }

          if (failedCount > 0 && successfulCount === 0) {
            toast({
              title: "邀請失敗",
              description: `${failedCount} 個邀請失敗，請檢查錯誤信息`,
              variant: "destructive",
            });
            return; // Don't close dialog if all failed
          }

          resetInviteForm();
          setIsInviteDialogOpen(false);
        },
        onError: (error) => {
          console.error("Error inviting users:", error);
          const errorMessage =
            error instanceof Error ? error.message : "邀請用戶失敗";
          toast({
            title: "錯誤",
            description: errorMessage,
            variant: "destructive",
          });
        },
      }
    );
  };

  /* ── helpers ─────────────────────────────────────────── */
  const getRoleDisplayName = (role: SystemUserRole) => {
    switch (role) {
      case SYSTEM_ROLES.ADMIN:
        return "管理員";
      case SYSTEM_ROLES.USER:
        return "一般用戶";
      default:
        return role;
    }
  };

  /* ── auth check ──────────────────────────────────────── */
  if (!loading && !isAdmin) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">權限不足</h2>
            <p className="text-muted-foreground">
              只有管理員可以訪問用戶管理頁面
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── loading state ───────────────────────────────────── */
  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">用戶管理</h1>
            <p className="text-muted-foreground">
              管理用戶帳號、權限和部門分配
            </p>
          </div>
          <Button disabled>
            <UserPlus className="w-4 h-4 mr-2" />
            邀請用戶
          </Button>
        </div>

        <div className="rounded-md border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">用戶</TableHead>
                  <TableHead className="min-w-[180px] hidden sm:table-cell">Email</TableHead>
                  <TableHead className="min-w-[120px]">角色</TableHead>
                  <TableHead className="min-w-[80px]">狀態</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="flex flex-col gap-1">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-20 sm:hidden" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[180px] hidden sm:table-cell">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell className="min-w-[120px]">
                      <Skeleton className="h-8 w-24 rounded" />
                    </TableCell>
                    <TableCell className="min-w-[80px]">
                      <Skeleton className="h-5 w-12 rounded-full" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-8 rounded ml-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  /* ── UI ───────────────────────────────────────────────── */
  return (
    <div className="container mx-auto py-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">用戶管理</h1>
          <p className="text-muted-foreground">管理用戶帳號、權限和部門分配</p>
        </div>

        <Dialog
          open={isInviteDialogOpen}
          onOpenChange={(open) => {
            setIsInviteDialogOpen(open);
            if (!open) resetInviteForm();
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="w-4 h-4 mr-2" />
              邀請用戶
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>邀請新用戶</DialogTitle>
              <DialogDescription>
                邀請新用戶加入您的組織，他們將收到邀請郵件
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-1">
              <div className="space-y-4 py-4">
                {/* Email inputs */}
                <div className="space-y-3">
                  <Label>Email地址</Label>
                  {inviteEmails.map((email, index) => (
                    <div key={index} className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Input
                          value={email}
                          placeholder="請輸入email地址"
                          onChange={(e) =>
                            handleEmailChange(index, e.target.value)
                          }
                          className={`flex-grow ${
                            emailErrors[index] ? "border-red-500" : ""
                          }`}
                          disabled={teamInviteMutation.isPending}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveEmail(index)}
                          disabled={
                            inviteEmails.length === 1 ||
                            teamInviteMutation.isPending
                          }
                          className="h-10 w-10 text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {emailErrors[index] && (
                        <p className="text-xs text-red-500 mt-1 ml-3">
                          {emailErrors[index]}
                        </p>
                      )}
                    </div>
                  ))}

                  <div className="flex justify-end pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddEmail}
                      disabled={
                        inviteEmails.length >= 20 ||
                        teamInviteMutation.isPending
                      }
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      新增Email
                    </Button>
                  </div>
                </div>

                {/* Role selection */}
                <div className="space-y-2">
                  <Label htmlFor="role">預設角色</Label>
                  <Select
                    value={selectedRole}
                    onValueChange={(value: SystemUserRole) =>
                      setSelectedRole(value)
                    }
                    disabled={teamInviteMutation.isPending}
                  >
                    <SelectTrigger id="role">
                      <SelectValue placeholder="選擇角色" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SYSTEM_ROLES.USER}>
                        一般用戶
                      </SelectItem>
                      <SelectItem value={SYSTEM_ROLES.ADMIN}>管理員</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="border-t pt-4 mt-4">
              <Button
                variant="outline"
                type="button"
                onClick={() => setIsInviteDialogOpen(false)}
                disabled={teamInviteMutation.isPending}
              >
                取消
              </Button>
              <Button
                type="button"
                onClick={inviteUsers}
                disabled={
                  teamInviteMutation.isPending ||
                  !inviteEmails.some((email) => email.trim())
                }
              >
                {teamInviteMutation.isPending ? "邀請中..." : "發送邀請"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">用戶</TableHead>
                <TableHead className="min-w-[180px] hidden sm:table-cell">Email</TableHead>
                <TableHead className="min-w-[120px]">角色</TableHead>
                <TableHead className="min-w-[80px]">狀態</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    尚無用戶數據
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    {/* User name with avatar */}
                    <TableCell className="min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {(user.username || user.email)[0].toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium">{user.username}</span>
                          <span className="text-xs text-muted-foreground sm:hidden">
                            {user.email}
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    {/* Email */}
                    <TableCell className="min-w-[180px] hidden sm:table-cell">
                      {user.email}
                    </TableCell>

                    {/* Role */}
                    <TableCell className="min-w-[120px]">
                      <Select
                        value={user.role}
                        onValueChange={(newRole: SystemUserRole) =>
                          updateUserRole(user.id, newRole)
                        }
                      >
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SYSTEM_ROLES.USER}>
                            一般用戶
                          </SelectItem>
                          <SelectItem value={SYSTEM_ROLES.ADMIN}>
                            管理員
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>

                    {/* Status */}
                    <TableCell className="min-w-[80px]">
                      <Badge 
                        variant="outline"
                        className={`text-xs px-2 py-1 border ${
                          user.isActive
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {user.isActive ? "啟用" : "停用"}
                      </Badge>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => toggleUserStatus(user.id, user.isActive)}
                          >
                            {user.isActive ? (
                              <>
                                <UserX className="mr-2 h-4 w-4" />
                                停用用戶
                              </>
                            ) : (
                              <>
                                <UserCheck className="mr-2 h-4 w-4" />
                                啟用用戶
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            disabled
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            刪除用戶
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
